import { mutateByPath } from "../../../utility";
import { ArsTableState, ArstActionSetScaffoldColWidth } from "../interface";
import {
  isArsTableColumnGroupLabel,
  isArsTableColumnOperationCell,
  isArsTableRowHeaderCell,
} from "../typeguards";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";
import { getColumnCells } from "./getColumnCells";

/**
 * Updates the width of a scaffold column in both the scaffold model and body data.
 *
 * @param state - Current ARS table state
 * @param action - Width update action for a scaffold column
 * @returns Updated ARS table state
 */
export const arst_setScaffoldColWidth = <T>(
  state: ArsTableState<T>,
  action: ArstActionSetScaffoldColWidth,
): ArsTableState<T> => {
  // Find correct target
  const colCells = getColumnCells(state.calculatedTableRows, action.colId);
  const targetCell = colCells.find(
    (cell) =>
      isArsTableRowHeaderCell(cell, state.settings.editorProps.isT) ||
      isArsTableColumnOperationCell(cell, state.settings.editorProps.isT) ||
      (isArsTableColumnGroupLabel(cell, state.settings.editorProps.isT) &&
        (cell.colSpan ?? 1) === 1),
  );

  if (!targetCell || !state.scaffoldModel) return state;

  // Update scaffold model with new column width
  const newScaffoldModel = mutateByPath(
    state.scaffoldModel,
    `/columns/${targetCell.columnIndex}/width`,
    action.width,
  );

  let newBodyData = state.bodyData;
  if (targetCell.dataPath.startsWith("/rowHeaders")) {
    newBodyData = mutateByPath(newBodyData, `${targetCell.dataPath}/width`, action.width);
  } else {
    const targetDataPath = arst_resolveTargetPath(targetCell.dataPath);
    newBodyData = mutateByPath(newBodyData, `${targetDataPath}/dimension`, action.width);
  }

  return {
    ...state,
    bodyData: newBodyData,
    scaffoldModel: newScaffoldModel,
    changed: true,
  };
};
